import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@components/Button";
import { FeatureFlag } from "@components/FeatureFlag/FeatureFlag";
import { Notification } from "@components/Notification/Notification";
import { useAddBillModal } from "@hooks/useAddBillModal";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { usePOAModal } from "@hooks/usePOAModal";
import { usePOAStatementConsentsStateQuery } from "@services/powersOfAttorney/queries";
import { type IVoucherSummary } from "@services/summaries/types";
import { formatPrice } from "@utils/units";

interface IJointBillEmptyProvidersStateProps {
  preselectedJointBillServiceId?: number;
  providerCategoryIds?: string[];
  variant?: "bill" | "overview";
  vouchers?: IVoucherSummary[];
}

export const JointBillEmptyProvidersMessage: React.FC<
  IJointBillEmptyProvidersStateProps
> = ({
  preselectedJointBillServiceId,
  providerCategoryIds,
  variant = "bill",
  vouchers,
}) => {
  const { t } = useTranslation();
  const { openAddBillModal } = useAddBillModal();
  const { openPOAModal } = usePOAModal();
  const { isPoaEnabled } = useFeatureConfig();

  const grantedDiscount = vouchers ? vouchers[0]?.amount : undefined;

  const { data: powerOfAttorneyConsent } =
    usePOAStatementConsentsStateQuery(isPoaEnabled);

  return (
    <div
      className={twMerge(
        "flex w-full flex-col items-center gap-8 py-10",
        variant === "overview" && "px-6 md:flex-row",
      )}
    >
      <img
        alt={t("jointBillEmptyProvidersMessage.title")}
        src="/images/bills/empty-providers-state.png"
      />
      <div
        className={twMerge(
          "flex flex-col items-center gap-6",
          variant === "overview" && "md:items-start",
        )}
      >
        <div
          className={twMerge(
            "flex flex-col items-center gap-3",
            variant === "overview" && "text-center md:items-start md:text-left",
          )}
        >
          <h2
            className={twMerge(
              "text-2xl",
              variant === "overview" && "text-lg md:text-2xl",
            )}
          >
            {t("jointBillEmptyProvidersMessage.title")}
          </h2>
          <p
            className={twMerge(
              "text-grey-dark",
              variant === "overview" && "text-sm md:text-base",
            )}
          >
            {t("jointBillEmptyProvidersMessage.description")}
          </p>
        </div>
        <div className="flex flex-col gap-11">
          {powerOfAttorneyConsent?.activeConsentsExist ? (
            <div
              className={twMerge(
                "flex w-full flex-row flex-wrap justify-center gap-4",
                variant === "overview" && "md:justify-normal",
              )}
            >
              <Button
                variant="secondary"
                onClick={() =>
                  openAddBillModal({ preselectedJointBillServiceId })
                }
              >
                {t("jointBillEmptyProvidersMessage.addBill.manual.button")}
              </Button>
              <Button
                onClick={() =>
                  openPOAModal({
                    shouldFetchAllRecommendations: true,
                  })
                }
              >
                {t("jointBillEmptyProvidersMessage.addBill.automatic.button")}
              </Button>
            </div>
          ) : (
            <Button
              onClick={() =>
                openAddBillModal({ preselectedJointBillServiceId })
              }
              aria-label={t("jointBillEmptyProvidersMessage.button")}
            >
              {t("jointBillEmptyProvidersMessage.button")}
            </Button>
          )}
          {grantedDiscount && (
            <Notification
              description={
                <Trans
                  i18nKey={
                    "jointBillEmptyProvidersMessage.voucher.notification"
                  }
                  values={{ amount: formatPrice(grantedDiscount) }}
                  components={{
                    b: <span className="font-semibold" />,
                  }}
                />
              }
              color="green"
            />
          )}
          {!!providerCategoryIds?.length && (
            <FeatureFlag flag="isProviderFilteringByCategoryEnabled">
              <div className="flex flex-col gap-2">
                <p
                  className={twMerge(
                    "text-grey-dark",
                    variant === "overview" && "text-sm md:text-base",
                  )}
                >
                  {t("jointBillEmptyProvidersMessage.categories.description")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {providerCategoryIds?.map((id) => (
                    <Button
                      key={id}
                      className="h-9 border-grey-hover font-normal"
                      variant="secondary"
                      onClick={() =>
                        openAddBillModal({
                          preselectedJointBillServiceId,
                          preselectedCategoryId: id,
                        })
                      }
                    >
                      {t(`common.providerCategories.${id}`)}
                    </Button>
                  ))}
                  <Button
                    className="h-9 border-grey-hover font-normal"
                    variant="secondary"
                    onClick={() =>
                      openAddBillModal({
                        preselectedJointBillServiceId,
                        preselectedCategoryId: "all",
                      })
                    }
                  >
                    {t(
                      "jointBillEmptyProvidersMessage.categories.allProviders",
                    )}
                  </Button>
                </div>
              </div>
            </FeatureFlag>
          )}
        </div>
      </div>
    </div>
  );
};
